import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RotateCcw } from 'lucide-react';
import type { AdminPreset, PresetVersion } from '../../api/imagePresets';
import { presetError } from '../../api/imagePresets';
import { usePresetVersions, useRollbackPreset } from '../../hooks';
import { getErrorDetail } from '../../utils/error';
import { Modal } from './common';
import { formatKst } from './format';

// 비교에 보여 줄 순서. 스냅샷은 DB 모양이라 분류는 category_id.
const KEYS = [
  'enabled',
  'name',
  'category_id',
  'description',
  'aspect',
  'prompt',
  'negative_prompt',
  'variables',
  'allow_text_in_image',
  'reference_images',
  'provider',
  'model',
  'quality',
  'order',
];

const show = (v: unknown): string =>
  v === null || v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v);

const Diff: React.FC<{ older: PresetVersion; newer: PresetVersion }> = ({ older, newer }) => {
  const { t } = useTranslation();
  const changed = KEYS.filter((k) => show(older.snapshot[k]) !== show(newer.snapshot[k]));
  if (changed.length === 0) return <p className="text-sm text-slate-500">{t('imagePresets.noDiff')}</p>;
  return (
    <div className="space-y-3">
      {changed.map((key) => (
        <div key={key}>
          <p className="text-xs font-semibold text-slate-500 mb-1 font-mono">{key}</p>
          <div className="grid gap-2 md:grid-cols-2">
            <pre className="whitespace-pre-wrap break-words rounded-lg bg-red-50 border border-red-100 p-2.5 text-xs text-red-900 max-h-72 overflow-y-auto">
              {show(older.snapshot[key])}
            </pre>
            <pre className="whitespace-pre-wrap break-words rounded-lg bg-emerald-50 border border-emerald-100 p-2.5 text-xs text-emerald-900 max-h-72 overflow-y-auto">
              {show(newer.snapshot[key])}
            </pre>
          </div>
        </div>
      ))}
    </div>
  );
};

const VersionHistory: React.FC<{
  preset: AdminPreset;
  canEdit: boolean;
  onClose: () => void;
  onRolledBack: (preset: AdminPreset) => void;
}> = ({ preset, canEdit, onClose, onRolledBack }) => {
  const { t } = useTranslation();
  const versions = usePresetVersions(preset.id);
  const rollback = useRollbackPreset();
  // 비교 대상 두 개. 기본은 가장 최근 두 버전.
  const [picked, setPicked] = useState<number[]>([]);
  const list = useMemo(() => versions.data ?? [], [versions.data]);
  const compare = picked.length === 2 ? picked : list.slice(0, 2).map((v) => v.version);
  const [newer, older] = [...compare].sort((a, b) => b - a).map((n) => list.find((v) => v.version === n));

  const toggle = (version: number) =>
    setPicked((prev) => (prev.includes(version) ? prev.filter((v) => v !== version) : [...prev.slice(-1), version]));

  const restore = async (toVersion: number) => {
    if (!window.confirm(t('imagePresets.confirmRollback', { version: toVersion }))) return;
    try {
      onRolledBack(await rollback.mutateAsync({ id: preset.id, toVersion, version: preset.version }));
    } catch {
      // 오류는 아래에 보인다.
    }
  };

  return (
    <Modal title={t('imagePresets.historyTitle', { name: preset.name })} onClose={onClose} wide>
      {versions.isLoading ? (
        <Loader2 className="animate-spin text-slate-400 mx-auto" />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[18rem_1fr]">
          <ol className="space-y-1.5">
            {list.map((v) => {
              const selected = compare.includes(v.version);
              return (
                <li key={v.version}>
                  <div
                    className={`rounded-lg border px-3 py-2 text-sm ${selected ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200'}`}
                  >
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" className="mt-1" checked={selected} onChange={() => toggle(v.version)} />
                      <span className="min-w-0 flex-1">
                        <span className="font-semibold text-slate-800">v{v.version}</span>
                        {v.version === preset.version && (
                          <span className="ml-1.5 badge badge-info">{t('imagePresets.current')}</span>
                        )}
                        {v.note && <span className="ml-1.5 text-slate-600">{v.note}</span>}
                        <span className="block text-xs text-slate-500">
                          {formatKst(v.created_at)} · {v.created_by ?? '-'}
                        </span>
                      </span>
                    </label>
                    {canEdit && v.version !== preset.version && v.note !== 'deleted' && (
                      <button
                        type="button"
                        onClick={() => restore(v.version)}
                        disabled={rollback.isPending}
                        className="mt-1.5 ml-6 inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                      >
                        <RotateCcw size={12} /> {t('imagePresets.rollbackTo', { version: v.version })}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
          <div className="min-w-0">
            {rollback.error && (
              <p className="mb-3 rounded-lg bg-red-50 border border-red-200 p-2.5 text-sm text-red-700">
                {presetError(rollback.error)?.error_code === 'IMAGE_PRESET_VERSION_CONFLICT'
                  ? t('imagePresets.rollbackConflict')
                  : getErrorDetail(rollback.error, t('imagePresets.saveFailed'))}
              </p>
            )}
            {older && newer ? (
              <>
                <p className="text-sm text-slate-600 mb-3">
                  {t('imagePresets.comparing', { older: older.version, newer: newer.version })}
                </p>
                <Diff older={older} newer={newer} />
              </>
            ) : (
              <p className="text-sm text-slate-500">{t('imagePresets.onlyOneVersion')}</p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default VersionHistory;
